'use client'

import { mockedDatabase } from '@/services/user.service'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import { type FormEvent, useTransition, useState, type FC } from 'react'
import Input from '../Form/Input'
import Button from '../Button'
import Link from 'next/link'
import { type CommonProps } from '@/utils/types'
import clsx from 'clsx'

/**
 * LoginForm Component
 *
 * This component handles user authentication through a form interface.
 * It uses Next.js's built-in authentication system (next-auth) and includes
 * error handling and loading states.
 *
 * Key features:
 * - Form validation for email and password
 * - Error message display
 * - Loading state during authentication
 * - Automatic redirection after successful login
 * - Mocked credentials for demonstration purposes
 *
 * @component
 * @param {CommonProps} props - Component props including className for styling
 */
const LoginForm: FC<CommonProps> = ({ className }) => {
  const router = useRouter()
  const [pending, startTransition] = useTransition()
  const [error, setError] = useState<string | null>(null)
  const classList = clsx('flex-1 basis-1/2 border-r pr-7 border-indigo-500 mt-8', className)

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    startTransition(async () => {
      const formData = new FormData(e.currentTarget)
      const email = formData.get('email')
      const password = formData.get('password')

      if (!email || !password) {
        setError('Invalid credentials')
        return
      }

      try {
        const res = await signIn('credentials', {
          email,
          password,
          redirect: false
        })

        if (res?.error) {
          setError('Invalid credentials')
          return
        }

        router.push('/assessment/customize')
        router.refresh()
      }
      catch {
        setError('An error occurred during login')
      }
    })
  }

  return (
    <form className={classList} onSubmit={handleSubmit}>
      <div className="space-y-6">
        {error && (
          <div className="text-red-500 text-center">{error}</div>
        )}
        <Input
          id="email"
          name="email"
          type="email"
          required
          defaultValue={mockedDatabase.users[0].email}
          label="Username"
          placeholder="User1"
        />
        <Input
          id="password"
          name="password"
          type="password"
          required
          defaultValue={mockedDatabase.users[0].password}
          label="Password"
          placeholder="***********"
        />
      </div>
      <Link href="#" className="underline text-xs" aria-label="Forgot password" prefetch>
        Forgot password?
      </Link>
      <div className="py-8 flex justify-center">
        <Button aria-label="Login" type="submit" variant="accent" disabled={pending}>
          <span className="px-9">{pending ? 'Logging in...' : 'Log in'}</span>
        </Button>
      </div>
    </form>
  )
}

export default LoginForm
